import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import db from '../services/db';
import { formatInlineToHtml } from '../utils/inlineMarkdown';
import RichTextSlideRenderer from './RichTextSlideRenderer';
import ExerciseLead, { EXERCISE_CARD_PAD } from './ExerciseLead';
import { speakEnglish, cancelSpeech } from '../services/tts';
import { cleanTextForTts } from '../../lib/tts/cleanTextForTts.js';
import { scorePronunciationCompare } from '../services/pronunciationCompare';
import './ExerciseGameUI.css';

function pickAudioMime() {
    const candidates = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/ogg;codecs=opus',
        'audio/ogg',
    ];
    if (typeof MediaRecorder === 'undefined') return '';
    return candidates.find((t) => MediaRecorder.isTypeSupported?.(t)) || '';
}

function extFromMime(mime) {
    const m = (mime || '').toLowerCase();
    if (m.includes('webm')) return 'webm';
    if (m.includes('ogg')) return 'ogg';
    if (m.includes('mp4') || m.includes('m4a')) return 'm4a';
    return 'webm';
}

export function parsePronunciationCompareLines(body) {
    return String(body || '')
        .split(/\r?\n/)
        .map((l) => l.trimEnd())
        .filter((l) => l.trim().length > 0);
}

function resolveMaxAttempts(exercise) {
    const raw = exercise?.assessment?.voiceCompareMaxAttempts;
    if (raw === null || raw === undefined || raw === '' || raw === 'unlimited') return null;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return null;
    return Math.round(n);
}

function resolvePassScore(exercise) {
    const n = Number(exercise?.assessment?.voiceComparePassScore);
    if (!Number.isFinite(n) || n < 0 || n > 100) return 65;
    return Math.round(n);
}

function isAlreadyDone(assignment) {
    const st = String(assignment?.status || '').toLowerCase();
    return st === 'completed' || st === 'checked';
}

function normalizeRecordings(answers, lineCount) {
    const src = answers?.recordings;
    const out = Array.from({ length: lineCount }, () => null);
    if (Array.isArray(src)) {
        for (let i = 0; i < lineCount; i += 1) {
            const r = src[i];
            if (r?.url) {
                out[i] = normalizeRecordingRow(r);
            }
        }
    } else if (src && typeof src === 'object') {
        for (let i = 0; i < lineCount; i += 1) {
            const r = src[String(i)] || src[i];
            if (r?.url) {
                out[i] = normalizeRecordingRow(r);
            }
        }
    }
    return out;
}

function normalizeRecordingRow(r) {
    return {
        url: r.url,
        path: r.path || null,
        attemptsUsed: Math.max(1, Number(r.attemptsUsed) || 1),
        score: r.score != null && Number.isFinite(Number(r.score)) ? Number(r.score) : null,
        passed: typeof r.passed === 'boolean' ? r.passed : null,
        transcript: r.transcript || null,
        errors: Array.isArray(r.errors) ? r.errors : null,
        breakdown: r.breakdown && typeof r.breakdown === 'object' ? r.breakdown : null,
        expectedPhones: Array.isArray(r.expectedPhones) ? r.expectedPhones : null,
        heardPhones: Array.isArray(r.heardPhones) ? r.heardPhones : null,
        scoreError: r.scoreError || null,
    };
}

function formatPhones(list) {
    if (!Array.isArray(list) || !list.length) return '';
    return list.map((p) => (typeof p === 'string' ? p : (p?.phone || p?.heard || p?.expected || ''))).filter(Boolean).join(' ');
}

/** Match shared Interactive card shell (negative-margin instruction/notes need overflow). */
const CARD = {
    background: '#ffffff',
    borderRadius: 20,
    padding: EXERCISE_CARD_PAD,
    border: '1.5px solid #e2e8f0',
    maxWidth: '100%',
    overflow: 'hidden',
    width: '100%',
    boxSizing: 'border-box',
};

function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = String(reader.result || '');
            const comma = result.indexOf(',');
            resolve(comma >= 0 ? result.slice(comma + 1) : result);
        };
        reader.onerror = () => reject(reader.error || new Error('Failed to read audio'));
        reader.readAsDataURL(blob);
    });
}

function LinePlayUrl({ media }) {
    const [playUrl, setPlayUrl] = useState(null);

    useEffect(() => {
        let cancelled = false;
        let blobUrl = null;
        (async () => {
            if (!media?.url && !media?.path) {
                setPlayUrl(null);
                return;
            }
            // Guest / open-exercise local blob URLs — play directly.
            if (typeof media.url === 'string' && media.url.startsWith('blob:')) {
                if (!cancelled) setPlayUrl(media.url);
                return;
            }
            try {
                const src = media.path || media.url;
                blobUrl = await db.downloadWritingCheckerMediaAsBlobUrl(src);
                if (!cancelled) setPlayUrl(blobUrl);
            } catch {
                try {
                    const fresh = await db.getFreshWritingCheckerMediaUrl(media.url);
                    if (!cancelled) setPlayUrl(fresh);
                } catch {
                    if (!cancelled) setPlayUrl(media.url);
                }
            }
        })();
        return () => {
            cancelled = true;
            if (blobUrl) URL.revokeObjectURL(blobUrl);
        };
    }, [media?.url, media?.path]);

    if (!playUrl) return null;
    return <audio controls src={playUrl} style={{ width: '100%', marginTop: 8 }} />;
}

/**
 * Pronunciation Compare: each body line → row with TTS model cell + student recording cell.
 */
export default function PronunciationCompareInteractive({
    exercise,
    assignment,
    preview,
    onComplete,
    reviewMode,
    onUpdate,
    readOnly,
    isOnlineLesson,
    user,
    isTeacher,
    practiceMode,
    completeLabel = 'Mark Done',
}) {
    const lines = useMemo(() => parsePronunciationCompareLines(exercise?.body), [exercise?.body]);
    const maxAttempts = useMemo(() => resolveMaxAttempts(exercise), [exercise]);
    const passScore = useMemo(() => resolvePassScore(exercise), [exercise]);
    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;
    const locked = !!(readOnly || reviewMode || alreadyDone || preview);
    const userId = user?.id || assignment?.student_id || assignment?.studentId;

    const [recordings, setRecordings] = useState(() => normalizeRecordings(assignment?.answers, lines.length));
    const [completed, setCompleted] = useState(alreadyDone);
    const [recordingIndex, setRecordingIndex] = useState(null);
    const [uploadingIndex, setUploadingIndex] = useState(null);
    const [scoringIndex, setScoringIndex] = useState(null);
    const [error, setError] = useState('');
    const [ttsBusyIndex, setTtsBusyIndex] = useState(null);

    const mediaRecorderRef = useRef(null);
    const streamRef = useRef(null);
    const chunksRef = useRef([]);
    const recordingsRef = useRef(recordings);

    useEffect(() => {
        recordingsRef.current = recordings;
    }, [recordings]);

    useEffect(() => {
        setRecordings(normalizeRecordings(assignment?.answers, lines.length));
    }, [assignment?.id, lines.length]);

    useEffect(() => () => {
        cancelSpeech();
        try {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.stop();
            }
        } catch { /* ignore */ }
        try {
            streamRef.current?.getTracks?.().forEach((tr) => tr.stop());
        } catch { /* ignore */ }
    }, []);

    const persistRecordings = useCallback((next, status) => {
        if (!onUpdate || isOnlineLesson) return;
        onUpdate({
            answers: {
                ...(assignment?.answers || {}),
                recordings: next,
            },
            status: status || (completed ? 'completed' : 'working'),
        });
    }, [onUpdate, isOnlineLesson, assignment?.answers, completed]);

    const stopStream = () => {
        try {
            streamRef.current?.getTracks?.().forEach((tr) => tr.stop());
        } catch { /* ignore */ }
        streamRef.current = null;
    };

    const stopRecording = useCallback(() => {
        try {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.stop();
            }
        } catch { /* ignore */ }
        setRecordingIndex(null);
    }, []);

    const applyScoreToRow = async (lineIndex, nextRow, scoreOpts) => {
        setScoringIndex(lineIndex);
        try {
            const result = await scorePronunciationCompare({
                ...scoreOpts,
                expectedText: cleanTextForTts(lines[lineIndex] || '', 'en-US'),
                lang: 'en',
                passScore,
            });
            return {
                ...nextRow,
                score: result.score,
                passed: result.passed,
                transcript: result.transcript || null,
                errors: Array.isArray(result.errors) ? result.errors : null,
                breakdown: result.breakdown || null,
                expectedPhones: Array.isArray(result.expectedPhones) ? result.expectedPhones : null,
                heardPhones: Array.isArray(result.heardPhones) ? result.heardPhones : null,
                scoreError: null,
            };
        } catch (scoreErr) {
            return {
                ...nextRow,
                scoreError: scoreErr?.message || 'Pronunciation check failed',
            };
        } finally {
            setScoringIndex(null);
        }
    };

    const finishRecordingUpload = async (lineIndex, blob, mimeType) => {
        if (!blob?.size) return;
        const prev = recordingsRef.current[lineIndex];
        const guestOpen = !userId || practiceMode === 'open';
        setUploadingIndex(lineIndex);
        setError('');
        try {
            const attemptsUsed = (prev?.attemptsUsed || 0) + 1;
            let nextRow;

            if (guestOpen) {
                if (prev?.url && String(prev.url).startsWith('blob:')) {
                    try { URL.revokeObjectURL(prev.url); } catch { /* ignore */ }
                }
                const localUrl = URL.createObjectURL(blob);
                nextRow = {
                    url: localUrl,
                    path: null,
                    attemptsUsed,
                    score: null,
                    passed: null,
                    transcript: null,
                    errors: null,
                    breakdown: null,
                    expectedPhones: null,
                    heardPhones: null,
                    scoreError: null,
                };
                const nextLocal = recordingsRef.current.map((r, i) => (i === lineIndex ? nextRow : r));
                setRecordings(nextLocal);
                setUploadingIndex(null);

                const expectedText = cleanTextForTts(lines[lineIndex] || '', 'en-US');
                if (expectedText) {
                    const audioBase64 = await blobToBase64(blob);
                    nextRow = await applyScoreToRow(lineIndex, nextRow, {
                        audioBase64,
                        mimeType: blob.type || mimeType || 'audio/webm',
                    });
                    const nextScored = recordingsRef.current.map((r, i) => (i === lineIndex ? nextRow : r));
                    setRecordings(nextScored);
                }
                return;
            }

            if (prev?.path) {
                try { await db.deleteWritingCheckerMedia(prev.path); } catch { /* ignore */ }
            }
            const ext = extFromMime(mimeType || blob.type);
            const file = new File([blob], `pronounce-${lineIndex}.${ext}`, {
                type: blob.type || mimeType || 'audio/webm',
            });
            const uploaded = await db.uploadWritingCheckerMedia(userId, file);
            if (!uploaded?.url) throw new Error('Upload did not return a URL.');
            nextRow = {
                url: uploaded.url,
                path: uploaded.path,
                attemptsUsed,
                score: null,
                passed: null,
                transcript: null,
                errors: null,
                breakdown: null,
                expectedPhones: null,
                heardPhones: null,
                scoreError: null,
            };
            const nextAfterUpload = recordingsRef.current.map((r, i) => (i === lineIndex ? nextRow : r));
            setRecordings(nextAfterUpload);
            persistRecordings(nextAfterUpload, 'working');
            setUploadingIndex(null);

            const expectedText = cleanTextForTts(lines[lineIndex] || '', 'en-US');
            if (expectedText && uploaded.url) {
                nextRow = await applyScoreToRow(lineIndex, nextRow, { audioUrl: uploaded.url });
                const nextScored = recordingsRef.current.map((r, i) => (i === lineIndex ? nextRow : r));
                setRecordings(nextScored);
                persistRecordings(nextScored, 'working');
            }
        } catch (e) {
            setError(e?.message || 'Upload failed');
            setUploadingIndex(null);
            setScoringIndex(null);
        }
    };

    const startRecording = async (lineIndex) => {
        if (locked || uploadingIndex != null || scoringIndex != null || recordingIndex != null) return;
        const existing = recordings[lineIndex];
        if (maxAttempts != null && (existing?.attemptsUsed || 0) >= maxAttempts) {
            setError(`Attempt limit reached for line ${lineIndex + 1}.`);
            return;
        }
        setError('');
        chunksRef.current = [];
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            const mimeType = pickAudioMime();
            const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
            mediaRecorderRef.current = mr;
            mr.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
            };
            mr.onstop = async () => {
                stopStream();
                const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
                chunksRef.current = [];
                await finishRecordingUpload(lineIndex, blob, mr.mimeType);
            };
            mr.start(250);
            setRecordingIndex(lineIndex);
        } catch (e) {
            stopStream();
            setError(e?.message || 'Microphone access denied');
        }
    };

    const playTts = (lineIndex, text) => {
        const spoken = cleanTextForTts(text, 'en-US');
        if (!spoken) return;
        setTtsBusyIndex(lineIndex);
        speakEnglish(spoken, {
            lang: 'en-US',
            onEnd: () => setTtsBusyIndex((cur) => (cur === lineIndex ? null : cur)),
            onError: () => setTtsBusyIndex((cur) => (cur === lineIndex ? null : cur)),
        });
    };

    const handleComplete = () => {
        setCompleted(true);
        const payload = {
            status: 'completed',
            score: 1,
            total: 1,
            answers: {
                ...(assignment?.answers || {}),
                recordings,
            },
        };
        if (onComplete) onComplete(payload);
        else if (onUpdate && !isOnlineLesson) onUpdate(payload);
    };

    const canShowComplete = !preview && !readOnly && !reviewMode
        && (!isOnlineLesson || isTeacher || practiceMode === 'individual' || practiceMode === 'open');
    const isMarkedDone = alreadyDone || completed;

    return (
        <div className="exercise-card-shell" style={CARD}>
            <ExerciseLead exercise={exercise} />

            {lines.length === 0 ? (
                <div style={{ color: '#94a3b8', fontWeight: 700, textAlign: 'center', padding: '1.5rem' }}>
                    No lines yet — add one word or sentence per line in the editor.
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {lines.map((line, i) => {
                        const rec = recordings[i];
                        const attemptsUsed = rec?.attemptsUsed || 0;
                        const atLimit = maxAttempts != null && attemptsUsed >= maxAttempts;
                        const canRecord = !locked && recordingIndex == null && uploadingIndex == null
                            && scoringIndex == null
                            && (!rec?.url || !atLimit);
                        const isRecording = recordingIndex === i;
                        const isUploading = uploadingIndex === i;
                        const isScoring = scoringIndex === i;
                        const html = formatInlineToHtml(line, `pc${i}`);

                        return (
                            <div
                                key={`${i}-${line.slice(0, 24)}`}
                                className="pc-row-grid"
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: 10,
                                    border: '1.5px solid #e2e8f0',
                                    borderRadius: 16,
                                    padding: 10,
                                    background: '#f8fafc',
                                }}
                            >
                                <div style={{
                                    background: 'white',
                                    borderRadius: 12,
                                    border: '1px solid #e2e8f0',
                                    padding: '12px 14px',
                                    minWidth: 0,
                                }}>
                                    <div style={{
                                        fontSize: '0.68rem', fontWeight: 800, color: '#94a3b8',
                                        textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8,
                                    }}>
                                        Model
                                    </div>
                                    <RichTextSlideRenderer
                                        content={html || '<br>'}
                                        style={{ lineHeight: 1.55, fontSize: '1.02rem', color: '#0f172a', marginBottom: 10 }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => playTts(i, line)}
                                        disabled={ttsBusyIndex === i}
                                        style={{
                                            border: 'none', borderRadius: 10, padding: '8px 12px',
                                            background: '#4361ee', color: 'white', fontWeight: 800,
                                            cursor: 'pointer', fontSize: '0.82rem',
                                        }}
                                    >
                                        {ttsBusyIndex === i ? '🔊 …' : '🔊 Play'}
                                    </button>
                                </div>

                                <div style={{
                                    background: 'white',
                                    borderRadius: 12,
                                    border: '1px solid #e2e8f0',
                                    padding: '12px 14px',
                                    minWidth: 0,
                                }}>
                                    <div style={{
                                        fontSize: '0.68rem', fontWeight: 800, color: '#94a3b8',
                                        textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8,
                                        display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap',
                                    }}>
                                        <span>Your recording</span>
                                        {maxAttempts != null && (
                                            <span style={{ color: atLimit ? '#b91c1c' : '#64748b' }}>
                                                {attemptsUsed}/{maxAttempts} attempts
                                            </span>
                                        )}
                                    </div>

                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', minHeight: 36 }}>
                                        {!isRecording && canRecord && (
                                            <button
                                                type="button"
                                                onClick={() => startRecording(i)}
                                                style={{
                                                    border: 'none', borderRadius: 10, padding: '8px 12px',
                                                    background: '#4361ee', color: 'white', fontWeight: 800,
                                                    cursor: 'pointer', fontSize: '0.82rem',
                                                }}
                                            >
                                                {rec?.url ? '🔄 Re-record' : '⏺ Record'}
                                            </button>
                                        )}
                                        {isRecording && (
                                            <button
                                                type="button"
                                                onClick={stopRecording}
                                                style={{
                                                    border: 'none', borderRadius: 10, padding: '8px 12px',
                                                    background: '#ef4444', color: 'white', fontWeight: 800,
                                                    cursor: 'pointer', fontSize: '0.82rem',
                                                }}
                                            >
                                                ⏹ Stop
                                            </button>
                                        )}
                                        {isUploading && (
                                            <span style={{ color: '#64748b', fontWeight: 700, fontSize: '0.8rem' }}>
                                                Uploading…
                                            </span>
                                        )}
                                        {isScoring && (
                                            <span style={{ color: '#64748b', fontWeight: 700, fontSize: '0.8rem' }}>
                                                Checking pronunciation…
                                            </span>
                                        )}
                                        {atLimit && rec?.url && !locked && (
                                            <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 700 }}>
                                                Attempts used up
                                            </span>
                                        )}
                                    </div>

                                    <LinePlayUrl media={rec} />

                                    {rec?.url && !isScoring && (rec.passed === true || rec.passed === false) && (
                                        <div style={{
                                            marginTop: 10, padding: '8px 10px', borderRadius: 10,
                                            background: rec.passed ? '#ecfdf5' : '#fef2f2',
                                            border: `1px solid ${rec.passed ? '#a7f3d0' : '#fecaca'}`,
                                            color: rec.passed ? '#047857' : '#b91c1c',
                                            fontWeight: 800, fontSize: '0.85rem',
                                        }}>
                                            {rec.passed ? '✓ Correct' : '✗ Try again'}
                                            {rec.score != null && (
                                                <span style={{ fontWeight: 700, opacity: 0.85 }}>
                                                    {' '}({Math.round(rec.score)}%)
                                                </span>
                                            )}
                                            {rec.transcript && (
                                                <div style={{
                                                    marginTop: 6, fontWeight: 700, fontSize: '0.75rem',
                                                    color: rec.passed ? '#065f46' : '#7f1d1d',
                                                    opacity: 0.95,
                                                }}>
                                                    Heard: {rec.transcript}
                                                </div>
                                            )}
                                            {rec.breakdown && (rec.breakdown.sounds != null || rec.breakdown.words != null) && (
                                                <div style={{
                                                    marginTop: 6, fontWeight: 700, fontSize: '0.72rem',
                                                    color: rec.passed ? '#047857' : '#7f1d1d',
                                                    opacity: 0.9, lineHeight: 1.4,
                                                }}>
                                                    Why not 100:
                                                    {rec.breakdown.sounds != null && ` sounds ${rec.breakdown.sounds}`}
                                                    {rec.breakdown.words != null && ` · ASR spelling ${rec.breakdown.words}`}
                                                    <div style={{ fontWeight: 600, fontSize: '0.68rem', opacity: 0.85, marginTop: 2 }}>
                                                        Grade is mostly sounds; ASR spelling is the transcript word match (can mis-hear seat as SEED).
                                                    </div>
                                                </div>
                                            )}
                                            {(formatPhones(rec.expectedPhones) || formatPhones(rec.heardPhones)) && (
                                                <div style={{
                                                    marginTop: 6, fontWeight: 700, fontSize: '0.7rem',
                                                    color: rec.passed ? '#065f46' : '#7f1d1d',
                                                    opacity: 0.9, lineHeight: 1.45,
                                                }}>
                                                    {formatPhones(rec.expectedPhones) && (
                                                        <div>Expected sounds: /{formatPhones(rec.expectedPhones)}/</div>
                                                    )}
                                                    {formatPhones(rec.heardPhones) && (
                                                        <div>Heard sounds: /{formatPhones(rec.heardPhones)}/</div>
                                                    )}
                                                </div>
                                            )}
                                            {Array.isArray(rec.errors) && rec.errors.length > 0 && (
                                                <div style={{
                                                    marginTop: 6, fontWeight: 700, fontSize: '0.75rem',
                                                    color: rec.passed ? '#065f46' : '#7f1d1d',
                                                }}>
                                                    {rec.errors.slice(0, 6).map((e) => (
                                                        <div key={`${e.word}-${e.expected}`}>
                                                            {e.word}: /{e.expected}/ → /{e.actual || '—'}/
                                                            {Array.isArray(e.phones) && e.phones.length > 0 && (
                                                                <span style={{ opacity: 0.85 }}>
                                                                    {' '}(
                                                                    {e.phones
                                                                        .filter((p) => p.expected !== p.heard)
                                                                        .slice(0, 4)
                                                                        .map((p) => `${p.expected || '∅'}→${p.heard || '∅'}`)
                                                                        .join(', ')}
                                                                    )
                                                                </span>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {rec?.scoreError && !isScoring && (
                                        <div style={{
                                            marginTop: 10, fontSize: '0.75rem', fontWeight: 700, color: '#b45309',
                                        }}>
                                            Saved recording, but check failed: {rec.scoreError}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {error && (
                <div style={{ color: '#b91c1c', fontWeight: 700, fontSize: '0.82rem', marginTop: 12 }}>{error}</div>
            )}

            {canShowComplete && (
                <div style={{ display: 'flex', gap: 10, marginTop: '1.1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    <button
                        type="button"
                        className={isMarkedDone
                            ? 'exercise-game-btn exercise-game-btn--marked-done'
                            : 'exercise-game-btn exercise-game-btn--complete'}
                        onClick={isMarkedDone ? undefined : handleComplete}
                        disabled={isMarkedDone || recordingIndex != null || uploadingIndex != null || scoringIndex != null}
                    >
                        {isMarkedDone ? 'Done ✅' : completeLabel}
                    </button>
                    {(assignment?.status || completed) && (
                        <span style={{ fontSize: '0.8rem', fontWeight: 800, color: '#64748b' }}>
                            Status: {assignment?.status || (completed ? 'completed' : 'working')}
                        </span>
                    )}
                </div>
            )}

            {isOnlineLesson && isTeacher && (
                <div style={{ marginTop: 12, fontSize: '0.8rem', color: '#64748b', fontWeight: 700 }}>
                    Pronunciation Compare is for homework / individual practice — use Assign, not live Shared mode.
                </div>
            )}

            <style>{`
                @media (max-width: 640px) {
                    .pc-row-grid { grid-template-columns: 1fr !important; }
                }
            `}</style>
        </div>
    );
}
